import { Deal } from 'models/deals'
import { DealReport } from 'models/deals_reports'
import { DataTypeColumn } from '../Common/commonColumnHelpers'

type CustomFields =
  | 'company'
  | 'category'
  | 'sub_category'
  | 'distributors'
  | 'gpos'
  | 'note_count'
  | 'contact_count'
  | 'days_since_last_modified'
  | 'days_since_deal_last_modified'
  | 'taglist'
  | 'product'
  | 'product_variant'
  | 'sku'
  | 'deal_name'
  | 'company_size'
  | 'identifier'
  // V2 aliases for backward compatibility
  | 'product_id'
  | 'product_variant_id'
  | 'sales_stage_id'
  | 'account_owner_id'
  | 'origin_campaign_id'
  | 'cc_category_id'
  | 'cc_sub_category_id'
  | 'cc_doors_cnt'
  | 'cc_taglist'
  | 'deal_taglist'
  | 'cc_distributors'
  | 'cc_gpos'
  | 'cc_note_count'
  | 'cc_contact_count'

export const DEAL_COLUMNS = {
  identifier: {
    header: 'Deal ID',
    group: 'Company Attributes',
  },
  company: {
    header: 'Company',
    group: 'Company Attributes',
  },
  product: {
    header: 'Product',
    group: 'Campaigns and Deals',
  },
  product_variant: {
    header: 'Product Variant',
    group: 'Campaigns and Deals',
  },
  sku: {
    header: 'SKU Number',
    group: 'Campaigns and Deals',
  },
  deal_name: {
    header: 'Deal Name',
    group: 'Campaigns and Deals',
  },
  company_size: {
    header: 'Company Size',
    group: 'Company Attributes',
    tooltipName: 'deals_chain_size',
  },
  category: {
    header: 'Category',
    group: 'Company Attributes',
  },
  sub_category: {
    header: 'Type',
    group: 'Company Attributes',
  },
  sales_stage: {
    header: 'Sales Stage',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_sales_stage',
  },
  last_datetime_stage_changed: {
    header: 'Days In Stage',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_last_datetime_stage_changed',
  },
  expected_days_to_close: {
    header: 'Expected Close Date',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_expected_days_to_close',
  },
  close_date_override: {
    header: 'Close Date (override)',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_close_date_override',
  },
  account_owner: {
    header: 'Account Owner',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_account_owner',
  },
  origin_campaign: {
    header: 'Origin Campaign',
    group: 'Campaigns and Deals',
  },

  // Revenue OPP
  annual_revenue: {
    header: '1yr Revenue',
    group: 'Forecast',
    tooltipName: 'deals_annual_revenue',
  },
  monthly_revenue_predicted: {
    header: 'Monthly Revenue',
    group: 'Forecast',
    tooltipName: 'deals_predicted_monthly_revenue',
  },
  monthly_revenue_override: {
    header: 'Revenue (override)',
    group: 'Forecast',
    tooltipName: 'deals_revenue_override',
  },

  // Volume
  annual_volume: {
    header: '1yr Volume',
    group: 'Forecast',
    tooltipName: 'deals_annual_volume',
  },
  monthly_volume_predicted: {
    header: 'Monthly Volume',
    group: 'Forecast',
    tooltipName: 'deals_predicted_volume',
  },
  monthly_volume_override: {
    header: 'Volume (override)',
    group: 'Forecast',
    tooltipName: 'deals_volume_override',
  },

  // Brand OPP
  annual_brand_opp: {
    header: '1yr Brand Value',
    group: 'Forecast',
  },

  // Total
  annual_total: {
    header: '1yr Total Value',
    group: 'Forecast',
  },

  taglist: {
    header: 'Company Tags',
    group: 'Company Attributes',
    tooltipName: 'deals_contact_company_tags',
  },
  deal_taglist: {
    header: 'Deal Tags',
    group: 'Campaigns and Deals',
  },
  contact_tags: {
    header: 'Contact Labels',
    group: 'Company Attributes',
    tooltipName: 'deals_contact_tags',
  },
  distributors: {
    header: 'Known Distributors',
    group: 'Company Attributes',
    tooltipName: 'deals_distributors',
  },
  gpos: {
    header: 'Known GPOs',
    group: 'Company Attributes',
    tooltipName: 'deals_gpos',
  },
  note_count: {
    header: 'Note Count',
    group: 'Company Attributes',
    tooltipName: 'deals_note_count',
  },
  contact_count: {
    header: 'Contact Count',
    group: 'Company Attributes',
    tooltipName: 'deals_contact_count',
  },
  days_since_last_modified: {
    header: 'Days Since Company Updated',
    group: 'Company Attributes',
    tooltipName: 'dealspipeline_days_since_last_modified',
  },
  days_since_deal_last_modified: {
    header: 'Days Since Deals Updated',
    group: 'Company Attributes',
    tooltipName: 'dealspipeline_days_since_deal_last_modified',
  },
  estimated_meals_per_day: {
    header: 'Estimated Meals Per Day',
    group: 'Company Attributes',
    tooltipName: 'deals_estimated_meals_per_day',
  },
  operational_schools: {
    header: 'School Count',
    group: 'Company Attributes',
    tooltipName: 'deals_operational_schools',
  },
  days_since_sent: {
    header: 'Days Since Sent',
    group: 'Company Attributes',
    tooltipName: 'deals_days_since_sent',
  },
  days_since_reply: {
    header: 'Days Since Reply',
    group: 'Company Attributes',
    tooltipName: 'deals_days_since_reply',
  },
  modified: {
    header: 'Last Modified',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_modified',
  },
  created: {
    header: 'Date Created',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_created',
  },
  doors_count: {
    header: 'Deal Size',
    group: 'Company Attributes',
    tooltipName: 'deals_doors_count',
  },

  // ========================================================================
  // V2 Field Name Aliases (for backward compatibility with old saved filters)
  // ========================================================================
  // Old saved filters created in V2 use field names like sales_stage_id,
  // but we now use V1 names (sales_stage). These aliases allow FilterChips
  // to display the correct labels for old saved filters.

  product_id: {
    header: 'Product',
    group: 'Campaigns and Deals',
  },
  product_variant_id: {
    header: 'Product Variant',
    group: 'Campaigns and Deals',
  },
  sales_stage_id: {
    header: 'Sales Stage',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_sales_stage',
  },
  account_owner_id: {
    header: 'Account Owner',
    group: 'Campaigns and Deals',
    tooltipName: 'deals_account_owner',
  },
  origin_campaign_id: {
    header: 'Origin Campaign',
    group: 'Campaigns and Deals',
  },
  cc_category_id: {
    header: 'Category',
    group: 'Company Attributes',
  },
  cc_sub_category_id: {
    header: 'Type',
    group: 'Company Attributes',
  },
  cc_doors_cnt: {
    header: 'Company Size',
    group: 'Company Attributes',
    tooltipName: 'deals_chain_size',
  },
  cc_taglist: {
    header: 'Company Tags',
    group: 'Company Attributes',
    tooltipName: 'deals_contact_company_tags',
  },
  cc_distributors: {
    header: 'Known Distributors',
    group: 'Company Attributes',
    tooltipName: 'deals_distributors',
  },
  cc_gpos: {
    header: 'Known GPOs',
    group: 'Company Attributes',
    tooltipName: 'deals_gpos',
  },
  cc_note_count: {
    header: 'Note Count',
    group: 'Company Attributes',
    tooltipName: 'deals_note_count',
  },
  cc_contact_count: {
    header: 'Contact Count',
    group: 'Company Attributes',
    tooltipName: 'deals_contact_count',
  },
} satisfies Partial<
  Record<
    | CustomFields
    | keyof Deal
    | keyof DealReport
    | keyof Deal['contact_company'],
    DataTypeColumn
  >
>
